import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Divider,
  Chip,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
} from "@mui/material";

import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Hotel as BedIcon,
  MeetingRoom as RoomIcon,
  PeopleAlt as StudentIcon,
  CheckCircleOutline as ActiveIcon,
  ExitToApp as LeftIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PRIMARY = "#0d9488";
const PRIMARY_LIGHT = "#ccfbf1";
const PRIMARY_DARK = "#0f766e";

export default function Roomallocation() {
  const auth = getAuth();

  const [allocations, setAllocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);

  const [hostelId, setHostelId] = useState("");
  const [schoolId, setSchoolId] = useState("");

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  // Search & Filter states
  const [search, setSearch] = useState("");
  const [selectedRoomFilter, setSelectedRoomFilter] = useState("ALL");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("ALL");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState({
    studentId: "",
    studentName: "",
    hostelId: "",
    roomId: "",
    bedno: "",
    joiningDate: new Date().toISOString().split("T")[0],
    status: "Live",
    schoolId: "",
  });

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      setLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const q = query(
        collection(db, "users"),
        where("uid", "==", currentUser.uid)
      );

      const snap = await getDocs(q);
      if (snap.empty) return;

      const userData = snap.docs[0].data();
      const userHostelId = userData.hostelId || "";
      const userSchoolId = userData.schoolId || "";
      const userGender = userData.gender || "";

      setHostelId(userHostelId);
      setSchoolId(userSchoolId);

      setForm((prev) => ({
        ...prev,
        hostelId: userHostelId,
        schoolId: userSchoolId,
      }));

      await fetchHostels(userHostelId);
      await fetchRooms(userHostelId);
      await fetchAllocations(userHostelId, userSchoolId);
      await fetchStudents(userSchoolId, userGender);
    } catch (error) {
      console.error("Error loading user profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async (targetHostelId) => {
    try {
      if (!targetHostelId) return [];

      const q = query(
        collection(db, "hostels"),
        where("__name__", "==", targetHostelId)
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().hostelName || "Unnamed Hostel",
      }));

      setHostels(data);
      return data;
    } catch (error) {
      console.error("Error fetching hostels:", error);
      return [];
    }
  };

  const fetchStudents = async (targetSchoolId, allowedGender) => {
    try {
      if (!targetSchoolId) return;

      const normalizedGender = String(allowedGender || "").trim().toUpperCase();

      const [admissionSnap, studentSnap, courseSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "admission"),
            where("schoolId", "==", targetSchoolId),
            where("studenttype", "==", "Boarding"),
            where("status", "==", "Active")
          )
        ),
        getDocs(
          query(collection(db, "students"), where("schoolId", "==", targetSchoolId))
        ),
        getDocs(
          query(collection(db, "courses"), where("schoolId", "==", targetSchoolId))
        ),
      ]);

      const studentMap = {};
      studentSnap.docs.forEach((d) => {
        studentMap[d.id] = d.data();
      });

      const courseMap = {};
      courseSnap.docs.forEach((d) => {
        courseMap[d.id] = d.data();
      });

      const data = admissionSnap.docs
        .map((adm) => {
          const admData = adm.data();
          const studentData = studentMap[admData.studentId] || {};
          const studentGender = String(studentData.gender || "").trim().toUpperCase();

          if (
            normalizedGender &&
            normalizedGender !== "CO-ED" &&
            studentGender !== normalizedGender
          ) {
            return null;
          }

          const className = courseMap[admData.class]?.name || "N/A";
          const studentName = `${studentData.firstname || ""} ${
            studentData.fathername || ""
          } ${studentData.surname || ""}`.trim();

          return {
            id: admData.studentId,
            name: studentName.toUpperCase(),
            label: `${studentName} - (${className})`.toUpperCase(),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));

      setStudents(data);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchRooms = async (targetHostelId) => {
    try {
      if (!targetHostelId) return [];

      const q = query(
        collection(db, "hostelrooms"),
        where("hostelId", "==", targetHostelId),
        where("status", "==", "Available")
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        roomNo: d.data().roomNo,
        noOfBeds: Number(d.data().noOfBeds || 0),
      }));

      // Sort numerical rooms properly
      data.sort((a, b) =>
        String(a.roomNo).localeCompare(String(b.roomNo), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );

      setRooms(data);
      return data;
    } catch (error) {
      console.error("Error fetching rooms:", error);
      return [];
    }
  };

  const fetchAllocations = async (targetHostelId, targetSchoolId) => {
    try {
      if (!targetHostelId || !targetSchoolId) return;

      const q = query(
        collection(db, "roomallocations"),
        where("hostelId", "==", targetHostelId),
        where("schoolId", "==", targetSchoolId)
      );

      const snap = await getDocs(q);

      const data = snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .sort((a, b) =>
          String(a.studentName || "").localeCompare(String(b.studentName || ""))
        );

      setAllocations(data);
    } catch (error) {
      console.error("Error fetching allocations:", error);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setBedOptions([]);

    setForm({
      studentId: "",
      studentName: "",
      hostelId,
      roomId: "",
      bedno: "",
      joiningDate: new Date().toISOString().split("T")[0],
      status: "Live",
      schoolId,
    });
  };

  const handleRoomChange = (roomNo, roomList = rooms) => {
    const selectedRoom = roomList.find(
      (room) => String(room.roomNo) === String(roomNo)
    );

    const beds = selectedRoom
      ? Array.from({ length: selectedRoom.noOfBeds }, (_, i) => i + 1)
      : [];

    setBedOptions(beds);

    setForm((prev) => ({
      ...prev,
      roomId: roomNo,
      bedno: "",
    }));
  };

  const handleSubmit = async () => {
    if (!form.studentId || !form.hostelId || !form.roomId || !form.bedno) {
      return Swal.fire(
        "Validation Error",
        "Please fill in all required fields",
        "warning"
      );
    }

    try {
      setLoading(true);

      const payload = {
        ...form,
        bedno: String(form.bedno),
        schoolId,
        hostelId,
        updatedAt: Timestamp.now(),
      };

      if (editId) {
        await updateDoc(doc(db, "roomallocations", editId), payload);
        Swal.fire("Success", "Allocation updated successfully", "success");
      } else {
        await addDoc(collection(db, "roomallocations"), {
          ...payload,
          createdAt: Timestamp.now(),
        });

        Swal.fire("Success", "Room allocated successfully", "success");
      }

      setOpen(false);
      resetForm();
      fetchAllocations(hostelId, schoolId);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (row) => {
    setEditId(row.id);

    const roomList = await fetchRooms(row.hostelId);
    const selectedRoom = roomList.find(
      (room) => String(room.roomNo) === String(row.roomId)
    );

    setBedOptions(
      selectedRoom
        ? Array.from({ length: selectedRoom.noOfBeds }, (_, i) => i + 1)
        : []
    );

    setForm({
      studentId: row.studentId || "",
      studentName: row.studentName || "",
      hostelId: row.hostelId || hostelId,
      roomId: row.roomId || "",
      bedno: row.bedno || "",
      joiningDate: row.joiningDate || new Date().toISOString().split("T")[0],
      status: row.status || "Live",
      schoolId: row.schoolId || schoolId,
    });

    setOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Allocation?",
      text: "Remove this student from the room?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "roomallocations", id));
      Swal.fire("Deleted", "Allocation removed successfully", "success");
      fetchAllocations(hostelId, schoolId);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  // Filter Logic (Student Name, Room No, Status)
  const filteredAllocations = allocations.filter((allocation) => {
    const matchesSearch = String(allocation.studentName || "")
      .toLowerCase()
      .includes(search.toLowerCase());

    const matchesRoom =
      selectedRoomFilter === "ALL" ||
      String(allocation.roomId) === String(selectedRoomFilter);

    const matchesStatus =
      selectedStatusFilter === "ALL" ||
      String(allocation.status).toLowerCase() ===
        selectedStatusFilter.toLowerCase();

    return matchesSearch && matchesRoom && matchesStatus;
  });

  // Unique assigned rooms list for filter dropdown
  const uniqueAllocatedRooms = Array.from(
    new Set(allocations.map((a) => String(a.roomId)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  // Summary Metrics
  const totalAllocationsCount = allocations.length;
  const liveCount = allocations.filter((a) => a.status === "Live").length;
  const leftCount = allocations.filter((a) => a.status === "Left").length;
  const uniqueRoomsCount = uniqueAllocatedRooms.length;

  const handleResetFilters = () => {
    setSearch("");
    setSelectedRoomFilter("ALL");
    setSelectedStatusFilter("ALL");
  };

  return (
    <Box p={1} sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
          color: "#fff",
          boxShadow: "0 10px 15px -3px rgba(13, 148, 136, 0.25)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <BedIcon sx={{ fontSize: 28 }} />
              <Typography variant="h5" fontWeight="700">
                Hostel Room Allocation
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage student bed assignments, room availability, and boarding status
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: "#ffffff",
              color: PRIMARY_DARK,
              fontWeight: "600",
              px: 2.5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
              "&:hover": {
                bgcolor: "#f1f5f9",
              },
            }}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            New Allocation
          </Button>
        </Stack>
      </Paper>

      {/* Summary Stat Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    TOTAL ALLOCATIONS
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="#1e293b" mt={0.5}>
                    {totalAllocationsCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "#e0f2fe",
                    color: "#0284c7",
                  }}
                >
                  <StudentIcon />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    ACTIVE BOARDERS
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="#16a34a" mt={0.5}>
                    {liveCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "#dcfce7",
                    color: "#16a34a",
                  }}
                >
                  <ActiveIcon />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    VACATED / LEFT
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color="#64748b" mt={0.5}>
                    {leftCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "#f1f5f9",
                    color: "#64748b",
                  }}
                >
                  <LeftIcon />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    OCCUPIED ROOMS
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color={PRIMARY_DARK} mt={0.5}>
                    {uniqueRoomsCount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: PRIMARY_LIGHT,
                    color: PRIMARY,
                  }}
                >
                  <RoomIcon />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Table Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Filter Controls Bar */}
        <Box p={2} sx={{ bgcolor: "#fff", borderBottom: "1px solid #f1f5f9" }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Student Search */}
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Student Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                  },
                }}
              />
            </Grid>

            {/* Room-Wise Search / Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Filter by Room"
                value={selectedRoomFilter}
                onChange={(e) => setSelectedRoomFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                <MenuItem value="ALL">All Rooms</MenuItem>
                {uniqueAllocatedRooms.map((rNo) => (
                  <MenuItem key={rNo} value={rNo}>
                    Room {rNo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="Live">Live</MenuItem>
                <MenuItem value="Left">Left</MenuItem>
              </TextField>
            </Grid>

            {/* Reset Filters */}
            <Grid item xs={12} sm={6} md={1.5}>
              <Tooltip title="Reset Filters">
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={handleResetFilters}
                  startIcon={<ClearIcon fontSize="small" />}
                  sx={{
                    borderRadius: 2,
                    color: "#64748b",
                    borderColor: "#cbd5e1",
                    height: 40,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      bgcolor: "#f1f5f9",
                    },
                  }}
                >
                  Reset
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table size="medium">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  Student Name
                </TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  Hostel
                </TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  Room No
                </TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  Bed No
                </TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  Joining Date
                </TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}>
                  Status
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "700", color: "#475569", py: 1.5 }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell align="center" colSpan={8} sx={{ py: 6 }}>
                    <CircularProgress size={32} sx={{ color: PRIMARY }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mt={1}
                      fontWeight="500"
                    >
                      Loading room allocations...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredAllocations.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={8} sx={{ py: 6 }}>
                    <Box sx={{ opacity: 0.6 }}>
                      <BedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                      <Typography variant="subtitle1" fontWeight="600" color="#64748b">
                        No Allocations Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try clearing or adjusting your search filters
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAllocations
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell sx={{ color: "#64748b", fontWeight: "500" }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      <TableCell sx={{ fontWeight: "600", color: "#0f172a" }}>
                        {row.studentName}
                      </TableCell>

                      <TableCell sx={{ color: "#475569" }}>
                        {hostels.find((h) => h.id === row.hostelId)?.name || "-"}
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={<RoomIcon style={{ fontSize: 14 }} />}
                          label={`Room ${row.roomId}`}
                          size="small"
                          sx={{
                            bgcolor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: "600",
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={`Bed ${row.bedno}`}
                          sx={{
                            bgcolor: PRIMARY_LIGHT,
                            color: PRIMARY_DARK,
                            fontWeight: "700",
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>

                      <TableCell sx={{ color: "#475569" }}>
                        {row.joiningDate}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          color={row.status === "Live" ? "success" : "default"}
                          variant={row.status === "Live" ? "filled" : "outlined"}
                          sx={{ fontWeight: "600", borderRadius: 1.5 }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Tooltip title="Edit Allocation">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              sx={{
                                color: PRIMARY,
                                bgcolor: PRIMARY_LIGHT,
                                "&:hover": { bgcolor: "#99f6e4" },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Allocation">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.id)}
                              sx={{
                                color: "#ef4444",
                                bgcolor: "#fee2e2",
                                "&:hover": { bgcolor: "#fca5a5" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredAllocations.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: "1px solid #f1f5f9" }}
        />
      </Paper>

      {/* Add / Edit Dialog Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3, p: 0.5 },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 2.5,
            display: "flex",
            alignItems: "center",
            fontWeight: "700",
            color: "#0f172a",
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: PRIMARY_LIGHT,
              color: PRIMARY,
              display: "flex",
              mr: 1.5,
            }}
          >
            <BedIcon fontSize="small" />
          </Box>
          {editId ? "Update Room Allocation" : "New Room Allocation"}
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            {/* Search Student Autocomplete */}
            <Grid item xs={12}>
              <Autocomplete
                options={students}
                size="small"
                value={
                  students.find((student) => student.id === form.studentId) || null
                }
                getOptionLabel={(option) => option.label || ""}
                onChange={(e, value) =>
                  setForm({
                    ...form,
                    studentId: value?.id || "",
                    studentName: value?.name || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Boarding Student *"
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ fontSize: "0.875rem" }}>
                    {option.label}
                  </li>
                )}
              />
            </Grid>

            {/* Hostel Select (Disabled / Auto-filled) */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Hostel"
                value={form.hostelId}
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              >
                {hostels.map((h) => (
                  <MenuItem key={h.id} value={h.id}>
                    {h.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Room Select */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Select Room *"
                value={form.roomId}
                onChange={(e) => handleRoomChange(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              >
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.roomNo}>
                    Room {room.roomNo} ({room.noOfBeds} Beds)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Bed Select */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Bed Number *"
                value={form.bedno}
                onChange={(e) => setForm({ ...form, bedno: e.target.value })}
                disabled={!form.roomId}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              >
                {bedOptions.map((bed) => (
                  <MenuItem key={bed} value={String(bed)}>
                    Bed {bed}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Joining Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Joining Date"
                InputLabelProps={{ shrink: true }}
                value={form.joiningDate}
                onChange={(e) =>
                  setForm({ ...form, joiningDate: e.target.value })
                }
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              >
                <MenuItem value="Live">Live</MenuItem>
                <MenuItem value="Left">Left</MenuItem>
              </TextField>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} mt={1}>
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setOpen(false)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    color: "#64748b",
                    borderColor: "#cbd5e1",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: PRIMARY,
                    "&:hover": { bgcolor: PRIMARY_DARK },
                    borderRadius: 2,
                    px: 3,
                    textTransform: "none",
                    fontWeight: "600",
                  }}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : editId ? (
                    "Update Allocation"
                  ) : (
                    "Save Allocation"
                  )}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
}